import {inject, injectable} from "inversify";
import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {Bindings} from "data/constants/bindings";
import type {IShareTeamPayload, ITeamApiProvider} from "data/providers/api/team.api.provider";
import type {ITeamStore} from "data/stores/team/team.store";
import {compact, size} from "lodash";
import {SharePayloadType} from "data/enums";

export interface IShareImage {
	link: string;
	preview: string;
}

const DEFAULT_SHARE_OBJECT: IShareImage = {
	link: "",
	preview: "",
};

export interface IShareStore {
	get isOpenShareModal(): boolean;
	get shareImagePath(): IShareImage;
	get getShareType(): SharePayloadType;

	toggleShareModal(): void;
	openShareModal(): void;
	closeShareModal(): void;
	shareTeam(payload: IShareTeamPayload): Promise<void>;
}

@injectable()
export class ShareStore implements IShareStore {
	@observable private _isOpenShareModal: boolean = false;
	@observable private _shareImagePath: IShareImage = DEFAULT_SHARE_OBJECT;

	get isOpenShareModal() {
		return this._isOpenShareModal;
	}

	get shareImagePath() {
		return this._shareImagePath;
	}

	get getShareType() {
		if (size(compact(this._teamStore.euLineup)) && size(compact(this._teamStore.usLineup))) {
			return SharePayloadType.Both;
		}
		if (size(compact(this._teamStore.euLineup))) {
			return SharePayloadType.Europe;
		}

		return SharePayloadType.Usa;
	}

	constructor(
		@inject(Bindings.TeamApiProvider) private _teamApi: ITeamApiProvider,
		@inject(Bindings.TeamStore) private _teamStore: ITeamStore
	) {
		makeAutoObservable(this);
	}

	@action toggleShareModal() {
		this._isOpenShareModal = !this._isOpenShareModal;
	}

	@action openShareModal() {
		this._isOpenShareModal = true;
	}

	@action closeShareModal() {
		this._isOpenShareModal = false;
		this._shareImagePath = DEFAULT_SHARE_OBJECT;
	}

	@action
	async shareTeam(payload: IShareTeamPayload) {
		try {
			const response = await this._teamApi.share(payload);
			const data = response.data.success;

			runInAction(() => {
				this._shareImagePath = data;
			});
		} catch (err) {
			console.log(err);
		}
	}
}
